import { useState, useEffect } from 'react'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card'
import { CheckCircle2, ArrowRight, Star, Zap } from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import { supabase } from '@/utils/supabase/client'
import { Subscription } from '@/types'

const plans = [
  { 
    name: 'Pro', 
    id: 'pro_monthly', 
    price: 5000, 
    price_id: 'price_1PMEPIGkflFrG6tozC3dYn1J',
    features: ['Up to 10 doctors', 'Up to 500 patients', 'Full feature access', 'Email support'],
    cta: 'Upgrade to Pro'
  },
  { 
    name: 'Enterprise', 
    id: 'enterprise_custom', 
    price: null,
    price_id: null,
    features: ['Unlimited doctors & patients', 'Dedicated support & SLA', 'Custom integrations', 'On-premise option'],
    cta: 'Contact Sales'
  },
]

export default function BillingPage() {
  const { currentOrganization } = useAuth()
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [loading, setLoading] = useState(true)
  const [managing, setManaging] = useState(false)

  useEffect(() => {
    if (currentOrganization) {
      fetchSubscription()
    } else {
      setLoading(false)
    }
  }, [currentOrganization])

  const fetchSubscription = async () => {
    if (!currentOrganization) return
    setLoading(true)
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('organization_id', currentOrganization.id)
      .single()
    
    if (data && !error) {
      setSubscription(data)
    }
    setLoading(false)
  }

  const handleManageSubscription = async () => {
    setManaging(true);
    // In the future, this will redirect to a payment provider's portal.
    setTimeout(() => {
      alert("Subscription management is not yet implemented.");
      setManaging(false);
    }, 2000);
  }

  const currentPlan = plans.find(p => p.id === subscription?.plan)
  
  const getNextBillingDate = () => {
    if (!subscription?.start_date) return "N/A"
    const startDate = new Date(subscription.start_date)
    startDate.setMonth(startDate.getMonth() + 1)
    return startDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  return (
    <div className="space-y-12">
      <Card className="bg-gradient-to-br from-blue-50 via-blue-50 to-white text-gray-800 overflow-hidden shadow-sm">
        <div className="p-8">
          <CardTitle className="text-3xl font-bold text-gray-900">
            {currentPlan ? `You're on the ${currentPlan.name} Plan` : "Welcome to Billing"}
          </CardTitle>
          <CardDescription className="text-gray-600 mt-2">
            {currentPlan 
              ? `Your subscription renews on ${getNextBillingDate()}.`
              : "You are not currently subscribed to any plan."}
          </CardDescription>
        </div>
        {currentPlan && subscription && (
          <CardContent className="px-8 pb-8 grid gap-8 md:grid-cols-3 items-start">
            <div className="md:col-span-2">
              <h3 className="text-xl font-semibold flex items-center text-gray-800"><Star className="h-5 w-5 mr-2 text-yellow-500" /> Plan Features</h3>
              <ul className="mt-4 space-y-3">
                {currentPlan.features.map(feature => (
                  <li key={feature} className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-3" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
             <div className="bg-white/60 rounded-lg p-6 backdrop-blur-sm border border-gray-200/50">
               <h3 className="text-xl font-semibold flex items-center text-gray-800">
                 <Zap className="h-5 w-5 mr-2 text-blue-500" />
                 Manage Subscription
               </h3>
               <p className="text-sm text-gray-600 mt-2">
                 Need to update your payment method or view past invoices?
               </p>
               <Button onClick={handleManageSubscription} className="mt-6 w-full bg-gray-900 text-white hover:bg-gray-800" disabled={managing}>
                 {managing ? "Loading Portal..." : "Go to Billing Portal"}
                 <ArrowRight className="ml-2 h-4 w-4" />
               </Button>
            </div>
          </CardContent>
        )}
      </Card>

      <div>
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">Ready to Upgrade?</h2>
        <p className="text-gray-600 mb-8 text-center">Choose a plan that scales with your clinic.</p>
        <div className="max-w-md mx-auto">
          {plans
            .filter(p => p.id !== currentPlan?.id)
            .map(plan => (
            <div key={plan.id} className="border bg-white rounded-xl p-8 flex flex-col hover:shadow-xl hover:border-blue-500 transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex-grow">
                <h3 className="text-2xl font-bold">{plan.name}</h3>
                <p className="mt-2 text-gray-500 text-lg">
                  {plan.price ? <><span className="font-bold text-gray-800">Rs. {plan.price.toLocaleString()}</span> / month</> : 'Custom Pricing'}
                </p>
                <ul className="mt-6 space-y-3">
                  {plan.features.map(feature => (
                    <li key={feature} className="flex items-center text-gray-700">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mr-3" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Button className="mt-8 w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg font-semibold" onClick={() => alert('Plan changing not implemented yet')}>
                {plan.cta} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          ))}
          {plans.filter(p => p.id !== currentPlan?.id).length === 0 && (
            <p className="text-gray-600 md:col-span-2 text-center">You are currently on the best plan available!</p>
          )}
        </div>
      </div>
    </div>
  )
} 